import React from 'react';
import {
    ExamStatus,
    AppType,
    ExamType,
    AnimatedTabs,
} from 'components';
import { IExamApplication } from 'components/models';
import { Tab } from 'react-bootstrap';
import { ApplicantsTab, IApplicantsTabProps } from './ApplicantsTab';
import { ExpertsTab } from './ExpertsTab';
import { ExamInfoTab, ExamInfoTabProps } from './ExamInfoTab';
import { RulesTab } from './RulesTab';
import { NokOrAkProtocolTab } from './NokOrAkProtocolTab';
import { SovietProtocolTab } from './SovietProtocolTab';
import { ISendProtocolProps, SendProtocolModal } from './SendProtocolModal';
import { VideoTab } from './VideoTab';
import { ExpertsSignProgress } from './ExpertsSignProgress';
import { IWebRTCReceiverType, Unread, VideoType } from '_helpers';

const ExamDetailsTabs = ({
    tab,
    applicantsTabProps,
    examDetails,
    examApplicationsList,
    withoutApplicantsTab,
    withoutExpertsTab,
    protocolModalProps,
    webRTC,
    unread,
    switchVideoType,
    onPreviewClick,
    appType,
    expertsSignProgressChild,
    handleExpertClick,
}: IProps) => {
    const isExpert = appType === AppType.Expert;
    const isSpkfr = appType === AppType.Spkfr;
    const isApplicant = appType === AppType.Applicant;
    const isQec = appType === AppType.Qec;

    const isProtocols = () => examStatus === ExamStatus.FinalizedResults
        || examStatus === ExamStatus.AkProtocolSigned
        || examStatus === ExamStatus.NokProtocolSigned
        || examStatus === ExamStatus.SovietProtocolSignedByMembers
        || examStatus === ExamStatus.SovietProtocolSignedByChairman;

    const checkPropsTab = () => {
        if (tab && (tab === TabKey.Rules
            || (tab === TabKey.Applicants && !withoutApplicantsTab)
            || tab === TabKey.Info
            || (tab === TabKey.Experts && !withoutExpertsTab))) {
            return tab;
        }

        return null;
    };

    const getDefaultTab = () => {
        if (checkPropsTab()) {
            return checkPropsTab();
        }

        if (!isExpert) return TabKey.Info;
        if (examStatus === ExamStatus.FinalizedResults) return TabKey.Nok;
        if (examStatus === ExamStatus.NokProtocolSigned) return TabKey.Ak;
        if (examStatus === ExamStatus.AkProtocolSigned
            || examStatus === ExamStatus.SovietProtocolSignedByMembers
            || examStatus === ExamStatus.SovietProtocolSignedByChairman) return TabKey.Soviet;
        if (examStatus === ExamStatus.IsPassing && examDetails && examDetails.type === ExamType.Online) return TabKey.Video;
        if (examStatus === ExamStatus.IsApplying) return TabKey.Info;
        if (!withoutApplicantsTab) return TabKey.Applicants;
        if (!withoutExpertsTab) return TabKey.Experts;

        return TabKey.Info;
    };

    React.useEffect(() => {
        setActiveTab(getDefaultTab());
    }, [examDetails]);

    const [activeTab, setActiveTab] = React.useState(getDefaultTab());
    const examStatus = examDetails.status;
    const examInCreateStatus = examStatus === ExamStatus.AwaitingExpertConfirmation;
    const showDetails = (isSpkfr || isApplicant || isQec) ? true : !isProtocols();
    const showNokOrAkProtocol = !isApplicant && !isQec && isProtocols() && examStatus !== ExamStatus.FinalizedResults;
    const showNokProtocol = !isApplicant && !isQec && examStatus === ExamStatus.FinalizedResults && isSpkfr;
    const showSovietProtocol = !isApplicant && !isQec && (examStatus === ExamStatus.AkProtocolSigned
        || examStatus === ExamStatus.SovietProtocolSignedByMembers
        || examStatus === ExamStatus.SovietProtocolSignedByChairman);

    const renderSendProtocolModal = () => {
        if (protocolModalProps && (
            examStatus === ExamStatus.FinalizedResults
            || (examStatus === ExamStatus.NokProtocolSigned && activeTab === TabKey.Ak)
            || ((examStatus === ExamStatus.AkProtocolSigned || examStatus === ExamStatus.SovietProtocolSignedByMembers) && activeTab === TabKey.Soviet)
        )) {
            return <SendProtocolModal examDetails={examDetails} {...protocolModalProps} />;
        }

        return null;
    };

    if (examStatus === ExamStatus.FinalizedResults && isExpert) {
        return (
            <>
                <NokOrAkProtocolTab
                  examDetails={examDetails}
                  examApplicationsList={examApplicationsList}
                  handleApplicantsListChange={applicantsTabProps ? applicantsTabProps.handleApplicantsListChange : () => true}
                />
                {renderSendProtocolModal()}
            </>
        );
    }

    return (
        <>
            <AnimatedTabs
              id="ExamDetailsTabs"
              activeKey={activeTab}
              onSelect={(key: string) => setActiveTab(key as TabKey)}
            >
                {isExpert && examDetails && examApplicationsList && examStatus === ExamStatus.IsPassing && examDetails.type === ExamType.Online ? (
                    <Tab eventKey={TabKey.Video} title="Проведение экзамена" className="tab mt-4 p-0">
                        <VideoTab
                          examApplications={examApplicationsList.items}
                          webRTC={webRTC}
                          unread={unread}
                          switchVideoType={switchVideoType}
                          onPreviewClick={onPreviewClick}
                        />
                    </Tab>
                ) : null}

                {showSovietProtocol ? (
                    <Tab eventKey={TabKey.Soviet} title="Решение совета" className="tab mt-4 p-0">
                        <SovietProtocolTab examDetails={examDetails} />
                    </Tab>
                ) : null}

                {showNokOrAkProtocol ? (
                    <Tab eventKey={TabKey.Ak} title="Протокол АК" className="tab mt-4 p-0">
                        <NokOrAkProtocolTab
                          examDetails={examDetails}
                          examApplicationsList={examApplicationsList}
                          handleApplicantsListChange={applicantsTabProps ? applicantsTabProps.handleApplicantsListChange : () => true}
                          asAkProtocol
                        />
                    </Tab>
                ) : null}

                {showNokOrAkProtocol || showNokProtocol ? (
                    <Tab eventKey={TabKey.Nok} title="Протокол НОК" className="tab mt-4 p-0">
                        <NokOrAkProtocolTab
                          examDetails={examDetails}
                          examApplicationsList={examApplicationsList}
                          handleApplicantsListChange={applicantsTabProps ? applicantsTabProps.handleApplicantsListChange : () => true}
                        />
                    </Tab>
                ) : null}

                {showDetails ? (
                    <Tab eventKey={TabKey.Info} title="Информация" className="tab mt-4 p-0">
                        <ExamInfoTab examDetails={examDetails} appType={appType} />
                    </Tab>
                ) : null}

                {!withoutApplicantsTab && applicantsTabProps && examApplicationsList && showDetails && !examInCreateStatus ? (
                    <Tab
                      eventKey={TabKey.Applicants}
                      title={examStatus === ExamStatus.ApplyingFinished && isExpert ? 'Допуск соискателей' : 'Список соискателей'}
                      className="tab mt-4 p-0"
                    >
                        <ApplicantsTab
                          examApplications={examApplicationsList.items}
                          examStatus={examStatus}
                          isExpert={isExpert}
                          handleApplicantClick={applicantsTabProps.handleApplicantClick}
                          handleApplicantsListChange={applicantsTabProps.handleApplicantsListChange}
                        />
                    </Tab>
                ) : null}

                {!withoutExpertsTab && showDetails ? (
                    <Tab eventKey={TabKey.Experts} title="Эксперты" className="tab mt-4 p-0">
                        {examInCreateStatus || examDetails.experts.some(c => c.acceptedInvite !== true) ? (
                            <ExpertsSignProgress experts={examDetails.experts} />
                        ) : (
                                <ExpertsTab experts={examDetails.experts} appType={appType} handleExpertClick={handleExpertClick} />
                            )}
                        {expertsSignProgressChild}
                    </Tab>
                ) : null}

                {showDetails ? (
                    <Tab eventKey={TabKey.Rules} title="Правила проведения" className="tab mt-4 p-0">
                        <RulesTab rules={examDetails.rules} />
                    </Tab>
                ) : null}
            </AnimatedTabs>
            {renderSendProtocolModal()}
        </>
    );
};

export default ExamDetailsTabs;

export interface IProps {
    examDetails: ExamInfoTabProps['examDetails'];
    appType: AppType;
    protocolModalProps?: ISendProtocolProps;
    examApplicationsList?: IExamApplicationList;
    applicantsTabProps?: IApplicantsTabProps;
    withoutApplicantsTab?: boolean;
    withoutExpertsTab?: boolean;
    expertsSignProgressChild?: React.ReactNode;
    tab?: TabType;
    webRTC?: {
        [id: string]: {
            receiver: IWebRTCReceiverType;
            connected: boolean;
            type: VideoType;
        };
    };
    unread?: { [id: string]: Unread };
    handleExpertClick?: (expertId: string) => void;
    switchVideoType?: (videoType: VideoType, applicantId: string) => void;
    onPreviewClick?: (applicationId: string) => void;
}

interface IExamApplicationList {
    admittedCount: number;
    items: IExamApplication[];
    page: number;
    pageSize: number;
    total: number;
}

enum TabKey {
    Applicants = 'applicants',
    Experts = 'experts',
    Info = 'info',
    Rules = 'rules',
    Ak = 'ak',
    Nok = 'nok',
    Soviet = 'soviet',
    Video = 'video',
}

type TabType = 'applicants' | 'experts' | 'info' | 'rules' | 'ak' | 'nok' | 'soviet' | 'video'
